// Central pricing calculation for the Apartments admin calculator.
// Deliberately simple: one price per apartment, one price per extra — no
// seasonal/date-based rules, no quotes. See ApartmentReservations Calculator tab.

export type PricingUnit =
  | "per_night"
  | "per_person"
  | "per_person_per_night"
  | "per_day"
  | "per_unit"
  | "per_reservation"
  | "per_transfer";

export const PRICING_UNITS: PricingUnit[] = [
  "per_night",
  "per_person",
  "per_person_per_night",
  "per_day",
  "per_unit",
  "per_reservation",
  "per_transfer",
];

export const PRICING_UNIT_LABELS: Record<PricingUnit, string> = {
  per_night: "po noći",
  per_person: "po osobi",
  per_person_per_night: "po osobi / noć",
  per_day: "po danu",
  per_unit: "po komadu",
  per_reservation: "po rezervaciji",
  per_transfer: "po transferu",
};

// Units whose quantity is derived automatically from nights/adults and isn't
// manually adjustable. Everything else (per_day, per_unit, per_transfer, or
// any legacy free-text unit) gets an editable quantity stepper, default 1.
const AUTO_QUANTITY_UNITS = new Set<string>([
  "per_night",
  "per_person",
  "per_person_per_night",
  "per_reservation",
]);

export const isManualQuantityUnit = (unit?: string | null) =>
  !AUTO_QUANTITY_UNITS.has(unit || "");

export const round2 = (n: number) => Math.round(n * 100) / 100;

export interface ExtraCalcInput {
  extraId: string;
  name: string;
  unit?: string | null;
  unitPrice: number;
  quantity?: number; // only read for manual-quantity units
  // For per_person / per_person_per_night: count children towards the person
  // count for this extra. Varies case by case, so it's decided per calculation
  // rather than stored permanently on the extra.
  includeChildren?: boolean;
}

export interface CalcLineItem {
  key: string;
  label: string;
  quantity: number;
  unitPrice: number;
  amount: number;
  unit: string;
  // Human-readable pieces of the "quantity" breakdown, e.g. ["2 osobe", "3 noći"]
  breakdownParts: string[];
  // Readable adults/children split when children were included in the person
  // count, e.g. "2 odraslih + 1 djece" — for display and for naming the
  // extra when it's copied onto a reservation, so the split isn't lost.
  personsNote?: string;
}

export interface CalculationInput {
  apartmentName?: string;
  pricePerNight: number;
  nights: number;
  adults: number;
  children?: number;
  extras: ExtraCalcInput[];
  discountPercent?: number;
}

export interface CalculationResult {
  nights: number;
  apartmentItem: CalcLineItem | null;
  extraItems: CalcLineItem[];
  subtotal: number;
  discountPercent: number;
  discountAmount: number;
  total: number;
}

export function computeExtraLine(
  extra: ExtraCalcInput,
  nights: number,
  adults: number,
  children: number = 0
): CalcLineItem {
  const unit = extra.unit || "per_unit";
  const countChildren = !!extra.includeChildren && children > 0;
  const persons = countChildren ? adults + children : adults;
  let quantity: number;
  let breakdownParts: string[];
  let personsNote: string | undefined;

  switch (unit) {
    case "per_night":
      quantity = nights;
      breakdownParts = [`${nights} noći`];
      break;
    case "per_person":
      quantity = persons;
      breakdownParts = [`${persons} osoba`];
      if (countChildren) personsNote = `${adults} odraslih + ${children} djece`;
      break;
    case "per_person_per_night":
      quantity = persons * nights;
      breakdownParts = [`${persons} osoba`, `${nights} noći`];
      if (countChildren) personsNote = `${adults} odraslih + ${children} djece`;
      break;
    case "per_reservation":
      quantity = 1;
      breakdownParts = [];
      break;
    default: // per_day, per_unit, per_transfer, or unrecognized legacy value
      quantity = Math.max(1, Math.round(extra.quantity ?? 1));
      breakdownParts = [`${quantity}x`];
      break;
  }

  return {
    key: extra.extraId,
    label: extra.name,
    quantity,
    unitPrice: extra.unitPrice,
    amount: round2(quantity * extra.unitPrice),
    unit,
    breakdownParts,
    personsNote,
  };
}

export function calculateReservationPrice(input: CalculationInput): CalculationResult {
  const nights = Math.max(0, input.nights);

  const apartmentItem: CalcLineItem | null =
    nights > 0
      ? {
          key: "apartment",
          label: input.apartmentName || "Apartment",
          quantity: nights,
          unitPrice: input.pricePerNight,
          amount: round2(input.pricePerNight * nights),
          unit: "per_night",
          breakdownParts: [`${nights} noći`],
        }
      : null;

  const extraItems = input.extras.map((e) =>
    computeExtraLine(e, nights, input.adults, input.children || 0)
  );

  const subtotal = round2(
    (apartmentItem?.amount || 0) + extraItems.reduce((s, e) => s + e.amount, 0)
  );
  const discountPercent = Math.min(100, Math.max(0, input.discountPercent || 0));
  const discountAmount = round2(subtotal * (discountPercent / 100));
  const total = round2(subtotal - discountAmount);

  return { nights, apartmentItem, extraItems, subtotal, discountPercent, discountAmount, total };
}
